import { FileText, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { AddEntryButtons } from "@/components/brand-context/AddEntryButtons";
import { BrandContextEntryCard } from "@/components/brand-context/BrandContextEntryCard";
import { BrandContextEntry } from "@/components/brand-context/entry";

interface EntryListProps {
  isLoading: boolean;
  entries: BrandContextEntry[];
  filteredEntries: BrandContextEntry[];
  readOnly: boolean;
  onAddLearning: () => void;
  onAddDocument: () => void;
  onOpenEntry: (entry: BrandContextEntry) => void;
  onDeleteEntry: (entry: BrandContextEntry) => void;
  onDownloadEntry: (entry: Extract<BrandContextEntry, { type: "document" }>) => void;
  downloadingDocumentId: string | null;
}

export function EntryList({
  isLoading,
  entries,
  filteredEntries,
  readOnly,
  onAddLearning,
  onAddDocument,
  onOpenEntry,
  onDeleteEntry,
  onDownloadEntry,
  downloadingDocumentId,
}: EntryListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (filteredEntries.length === 0) {
    return (
      <Card className="p-12 flex flex-col items-center text-center">
        <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center mb-3">
          <FileText className="h-6 w-6 text-muted-foreground" />
        </div>
        {entries.length === 0 ? (
          <>
            <h3 className="text-lg font-semibold text-foreground">No context yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Upload a brand voice document or capture a learning from a recent campaign to
              start building this brand's knowledge base.
            </p>
            {!readOnly && (
              <div className="flex items-center gap-2 mt-4">
                <AddEntryButtons onAddLearning={onAddLearning} onAddDocument={onAddDocument} />
              </div>
            )}
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-foreground">No entries match your filters</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Try a different search term or clear the type/category filters.
            </p>
          </>
        )}
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {filteredEntries.map((entry) => (
        <BrandContextEntryCard
          key={`${entry.type}-${entry.id}`}
          entry={entry}
          onOpen={() => onOpenEntry(entry)}
          onDelete={() => onDeleteEntry(entry)}
          onDownload={entry.type === "document" ? () => onDownloadEntry(entry) : undefined}
          isDownloading={entry.type === "document" && downloadingDocumentId === entry.id}
        />
      ))}
    </div>
  );
}
